import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { collection, addDoc, doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import { createRazorpayOrder, verifyRazorpayPayment } from '../utils/payments';
import { calculateCartGST } from '../utils/gstUtils';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, total, subtotal, discount, appliedCoupon, clearCart, shipping } = useCart();
  const { currentUser, userProfile } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const prefilledRef = useRef(false);

  // Autofill from profile
  useEffect(() => {
    if (!currentUser) return;
    // Only prefill once or when values are empty
    if (prefilledRef.current) return;
    setShippingAddress((prev) => {
      const to10 = (p) => {
        if (!p) return '';
        const only = String(p).replace(/\D/g, '');
        // Handle +91
        if (only.length === 12 && only.startsWith('91')) return only.slice(-10);
        if (only.length === 10) return only;
        return p;
      };
      const next = {
        name: prev.name || userProfile?.fullName || currentUser.displayName || '',
        phone: prev.phone || userProfile?.phone || to10(currentUser.phoneNumber) || '',
        email: prev.email || userProfile?.email || currentUser.email || '',
        address: prev.address || userProfile?.address || '',
        city: prev.city || userProfile?.city || '',
        state: prev.state || userProfile?.state || '',
        pincode: prev.pincode || userProfile?.pincode || '',
      };
      // Mark as prefilled only if we actually filled something
      const filledAny = Object.keys(next).some((k) => next[k] && next[k] !== prev[k]);
      if (filledAny) prefilledRef.current = true;
      return next;
    });
  }, [currentUser, userProfile]);
  
  // Calculate GST for the checkout
  const gstAmount = calculateCartGST(total);

  // Redirect if cart is empty
  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max">
            <div className="text-center py-20">
              <h1 className="text-4xl font-serif text-deep-brown mb-4">Your Cart is Empty</h1>
              <p className="text-warm-gray mb-8">Add some items to your cart before proceeding to checkout.</p>
              <button
                onClick={() => navigate('/')}
                className="bg-deep-brown text-cream px-8 py-3 rounded-md hover:bg-warm-gray transition duration-300 font-medium"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const required = ['name', 'phone', 'email', 'address', 'city', 'state', 'pincode'];
    const missing = required.filter(field => !shippingAddress[field].trim());
    
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      return false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingAddress.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate phone
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(shippingAddress.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }

    // Validate pincode
    const pincodeRegex = /^[0-9]{6}$/;
    if (!pincodeRegex.test(shippingAddress.pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return false;
    }

    return true;
  };

  const createOrder = async () => {
    try {
      const orderData = {
        userId: currentUser?.uid || null,
        customerEmail: shippingAddress.email,
        items: cartItems,
        shippingAddress,
        subtotal,
        discount,
        shipping,
        total,
        appliedCoupon: appliedCoupon || null,
        paymentMethod,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;

    if (!currentUser) {
      toast.error('Please login to place an order');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      // Save shipping details into user profile for future use
      try {
        if (currentUser?.uid) {
          await setDoc(doc(db, 'users', currentUser.uid), {
            uid: currentUser.uid,
            email: currentUser.email || shippingAddress.email,
            fullName: shippingAddress.name,
            phone: shippingAddress.phone,
            address: shippingAddress.address,
            city: shippingAddress.city,
            state: shippingAddress.state,
            pincode: shippingAddress.pincode,
            defaultShipping: {
              name: shippingAddress.name,
              phone: shippingAddress.phone,
              email: shippingAddress.email,
              address: shippingAddress.address,
              city: shippingAddress.city,
              state: shippingAddress.state,
              pincode: shippingAddress.pincode,
            },
            updatedAt: new Date(),
          }, { merge: true });
        }
      } catch (e) {
        // Non-blocking: continue checkout even if saving profile fails
        console.warn('Failed to save shipping to profile:', e);
      }

      // 1) Create order in Firestore as pending
      const orderId = await createOrder();

      if (paymentMethod === 'cod') {
        // Mark as pending COD; no online payment
        await updateDoc(doc(db, 'orders', orderId), {
          status: 'pending',
          paymentMethod: 'cod',
          paymentStatus: 'pending',
        });
        toast.success('Order placed with Cash on Delivery!');
        clearCart();
        navigate('/orders', { state: { orderId } });
        return;
      }

      // 2) Online payment via Razorpay
      const amountPaise = Math.round(total * 100); // Razorpay expects paise
      if (amountPaise < 100) {
        toast.error('Minimum payment amount is ₹1');
        return;
      }
      const receipt = `order_${orderId}`;

      // Create Razorpay order from backend
      const rzpOrder = await createRazorpayOrder({
        amount: amountPaise,
        currency: 'INR',
        receipt,
        notes: {
          orderId,
          userId: currentUser.uid,
          customerEmail: shippingAddress.email,
        },
      });

      // Load Razorpay key id from env
      const key = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
      if (!key) {
        toast.error('Razorpay key is not configured');
        return;
      }

      // Ensure the Razorpay script is loaded
      const ensureRzpLoaded = () => new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
        document.body.appendChild(script);
      });
      try {
        await ensureRzpLoaded();
      } catch (e) {
        toast.error('Payment SDK failed to load. Please try again.');
        return;
      }

      // Prepare options
      const options = {
        key,
        amount: rzpOrder.amount, // in paise
        currency: rzpOrder.currency,
        name: 'House of Deepak',
        description: 'Order Payment',
        order_id: rzpOrder.id,
        prefill: {
          name: shippingAddress.name,
          email: shippingAddress.email,
          contact: shippingAddress.phone,
        },
        notes: {
          orderId,
        },
        theme: {
          color: '#2C1810',
        },
        handler: async function (response) {
          try {
            const { razorpay_order_id, razorpay_payment_id, razorpay_signature } = response;
            // Verify signature with backend
            const verifyRes = await verifyRazorpayPayment({
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
            });

            if (!verifyRes.verified) {
              throw new Error('Payment verification failed');
            }

            // Update Firestore order with payment success
            await updateDoc(doc(db, 'orders', orderId), {
              status: 'pending',
              paymentMethod: 'razorpay',
              paymentStatus: 'paid',
              razorpay: {
                orderId: razorpay_order_id,
                paymentId: razorpay_payment_id,
                signature: razorpay_signature,
                rzpOrderId: rzpOrder.id,
              },
              updatedAt: new Date(),
            });

            toast.success('Payment successful! Order placed.');
            clearCart();
            navigate('/orders', { state: { orderId } });
          } catch (err) {
            console.error('Payment success but post-processing failed:', err);
            toast.error('Payment captured, but order update failed. Contact support.');
          }
        },
        modal: {
          ondismiss: async () => {
            // User closed the modal. Keep order as pending/unpaid.
            await updateDoc(doc(db, 'orders', orderId), {
              paymentMethod: 'razorpay',
              paymentStatus: 'cancelled',
              status: 'pending',
              updatedAt: new Date(),
            });
            toast('Payment cancelled');
          },
        },
      };

      // Open Razorpay modal
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', async function (response) {
        console.error('Payment failed:', response.error);
        await updateDoc(doc(db, 'orders', orderId), {
          paymentMethod: 'razorpay',
          paymentStatus: 'failed',
          status: 'pending',
          updatedAt: new Date(),
          razorpayError: response.error,
        });
        toast.error('Payment failed. Please try again.');
      });
      rzp.open();
      
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Error placing order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-serif text-deep-brown mb-8">Checkout</h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Shipping Information */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm p-6 border border-warm-gray/20">
                  <h2 className="text-xl font-serif font-medium text-deep-brown mb-6">Shipping Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={shippingAddress.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={shippingAddress.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        placeholder="10-digit phone number"
                        required
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={shippingAddress.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        required
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Address *
                      </label>
                      <textarea
                        name="address"
                        value={shippingAddress.address}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        placeholder="Street address, apartment, suite, etc."
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={shippingAddress.state}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        required
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-deep-brown mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        value={shippingAddress.pincode}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                        placeholder="6-digit pincode"
                        maxLength={6}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white rounded-lg shadow-sm p-6 border border-warm-gray/20 mt-6">
                  <h2 className="text-xl font-serif font-medium text-deep-brown mb-6">Payment Method</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="razorpay"
                        name="paymentMethod"
                        value="razorpay"
                        checked={paymentMethod === 'razorpay'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="h-4 w-4 text-gold focus:ring-gold border-warm-gray/30"
                      />
                      <label htmlFor="razorpay" className="ml-3 block text-sm font-medium text-deep-brown">
                        Pay with Razorpay (Cards, UPI, Net Banking, Wallets)
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="cod"
                        name="paymentMethod"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="h-4 w-4 text-gold focus:ring-gold border-warm-gray/30"
                      />
                      <label htmlFor="cod" className="ml-3 block text-sm font-medium text-deep-brown">
                        Cash on Delivery (COD)
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24 border border-warm-gray/20">
                  <h2 className="text-xl font-serif font-medium text-deep-brown mb-6">Order Summary</h2>
                  
                  {/* Order Items */}
                  <div className="space-y-4 mb-6">
                    {cartItems.map((item) => (
                      <div key={`${item.id}-${item.selectedSize}`} className="flex items-center space-x-3">
                        <img
                          src={item.images?.[0] || '/placeholder-image.jpg'}
                          alt={item.title}
                          className="h-12 w-12 object-cover rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-deep-brown truncate">{item.title}</p>
                          <p className="text-xs text-warm-gray">
                            {item.selectedSize && `Size: ${item.selectedSize} • `}Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="text-sm font-medium text-deep-brown">₹{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>

                  {/* Price Breakdown */}
                  <div className="border-t border-warm-gray/20 pt-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Subtotal</span>
                      <span className="font-medium text-deep-brown">₹{subtotal.toLocaleString()}</span>
                    </div>
                    
                    {appliedCoupon && discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span className="text-sm">Discount ({appliedCoupon.code})</span>
                        <span className="font-medium">-₹{discount.toLocaleString()}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Shipping</span>
                      {shipping === 0 ? (
                        <span className="text-sage">Free</span>
                      ) : (
                        <span className="font-medium text-deep-brown">₹{shipping.toLocaleString()}</span>
                      )}
                    </div>
                    
                    <div className="border-t border-warm-gray/20 pt-3">
                      <div className="flex justify-between text-lg font-semibold">
                        <span className="text-deep-brown">Total</span>
                        <span className="text-deep-brown">₹{total.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-xs text-warm-gray mt-1">
                        <span>GST Included</span>
                        <span>₹{gstAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Place Order Button */}
                  <button
                    onClick={handlePlaceOrder}
                    disabled={loading}
                    className="mt-6 w-full bg-gold text-deep-brown px-6 py-3 rounded-md font-semibold hover:bg-yellow-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Placing Order...' : 'Place Order'}
                  </button>
                  
                  <button
                    onClick={() => navigate('/cart')}
                    className="mt-3 w-full border border-deep-brown text-deep-brown px-6 py-3 rounded-md font-medium hover:bg-deep-brown hover:text-cream transition duration-300"
                  >
                    Back to Cart
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;